'use client';

import { useConfig } from '@packages/shared/src/hooks/useConfig/useConfig';
import { ErrorBoundary } from '@packages/shared/src/ui/ErrorBoundary/ErrorBoundary';
import { cmsFetcher } from '@packages/shared/src/utils/cmsFetcher/cmsFetcher';
import { Suspense } from 'react';
import { useIntl } from 'react-intl';
import useSWR from 'swr';
import { ServiceLayerItem } from './ServiceLayerItem';
import { ServiceLayerLoader } from './ServiceLayerLoader';
import type { CmsDataJsonApi } from './types';

/**
 * This component displays Service Layer Content on Product Detailview Pages
 *
 * @param serviceLayerId is the identifier to get content
 * @returns JSX.Element to be placed in PageLayout
 */

export type ServiceLayerProps = {
  serviceLayerId: string;
};

const ServiceLayerDataWrapper = ({ serviceLayerId }: ServiceLayerProps) => {
  const { locale } = useIntl();

  const {
    serviceLayer: { apiEndpoints },
  } = useConfig();

  // Fetch data from the Service Layer API using the useSWR hook and save it to dataPrimary and errorPrimary
  const { data, error } = useSWR<CmsDataJsonApi>(
    `${apiEndpoints.api}/${serviceLayerId}?locale=${locale}`,
    cmsFetcher,
    {
      suspense: true, // Show a fallback UI while waiting for data to load
    },
  );

  // If there's an error or no data or an empty data array, throw the error
  if (error || !data?.data || data.data?.length === 0) {
    throw error;
  }

  return <ServiceLayerItem serviceLayerData={data.data} />;
};

export const ServiceLayer = ({ serviceLayerId }: ServiceLayerProps) => (
  <ErrorBoundary>
    <Suspense fallback={<ServiceLayerLoader />}>
      <ServiceLayerDataWrapper serviceLayerId={serviceLayerId} />
    </Suspense>
  </ErrorBoundary>
);
